import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { App } from "./App";
import { LoginPage } from "./components/auth/LoginPage";
import { RegisterPage } from "./components/auth/RegisterPage";
import { ForgotPage } from "./components/auth/ForgotPage";
import { ResetPage } from "./components/auth/ResetPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminPage } from "./components/admin/AdminPage";
import { AdminInvitesTab } from "./components/admin/AdminInvitesTab";
import { AdminUsersTab } from "./components/admin/AdminUsersTab";
import { AdminVocabTab } from "./components/admin/AdminVocabTab";
import { AdminUsageTab } from "./components/admin/AdminUsageTab";
import { ImprintPage } from "./components/legal/ImprintPage";
import { PrivacyPage } from "./components/legal/PrivacyPage";
import { DisclaimerPage } from "./components/legal/DisclaimerPage";
import { VerifyPage } from "./components/auth/VerifyPage";
import { HelpPage } from "./components/HelpPage";
import { CertificatePage } from "./components/CertificatePage";
import { WordsPage } from "./components/WordsPage";
import { useAuthStore } from "./state/authStore";
import { drainOutbox, installSyncManager } from "./lib/sync";
import { AppLoadingScreen, useBootHold } from "./components/AppLoadingScreen";

// Stats tab pulls in recharts (~50 KB gzipped). Lazy-load so the main quiz
// bundle stays small for the dominant non-admin user.
const AdminStatsTab = lazy(() => import("./components/admin/AdminStatsTab"));

function StatsFallback() {
  return <p className="text-fg-muted text-sm animate-pulse">Lade Statistik-Modul…</p>;
}

/**
 * Initialises the auth store + sync manager, AND renders the boot splash
 * for the first MIN_BOOT_HOLD_MS regardless of which route is mounted.
 * Living above <Routes> means the splash shows on /login + /register + every
 * other public route too — not just protected routes (where ProtectedRoute
 * used to handle it).
 */
function AuthBootstrap() {
  const init = useAuthStore((s) => s.init);
  const status = useAuthStore((s) => s.status);
  const bootHold = useBootHold();

  useEffect(() => {
    installSyncManager();
    void init();
  }, [init]);
  useEffect(() => {
    if (status === "authed") void drainOutbox();
  }, [status]);

  if (status === "checking" || bootHold) {
    const label = status === "checking" ? "Authentifiziere…" : "Lade Vokabeln & Fortschritt…";
    return <AppLoadingScreen label={label} />;
  }
  return null;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AuthBootstrap />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot" element={<ForgotPage />} />
        <Route path="/reset" element={<ResetPage />} />
        {/*
          /impressum + /datenschutz are PUBLIC routes — § 5 TMG requires the
          Impressum to be reachable from any page in ≤ 2 clicks, which
          excludes gating them behind auth.
        */}
        <Route path="/impressum" element={<ImprintPage />} />
        <Route path="/datenschutz" element={<PrivacyPage />} />
        <Route path="/haftung" element={<DisclaimerPage />} />
        {/*
          /verify is public so the link in the welcome / reminder mail works
          even if the user is logged out on the device that opens it (e.g.
          they hit "verify" on a tablet after signing up on a phone).
        */}
        <Route path="/verify" element={<VerifyPage />} />
        {/*
          /help is public so the welcome mail (or any onboarding link) can
          drop users straight onto the explainer without forcing login.
        */}
        <Route path="/help" element={<HelpPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <App view="quiz" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stats"
          element={
            <ProtectedRoute>
              <App view="stats" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zertifikat"
          element={
            <ProtectedRoute>
              <CertificatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/words"
          element={
            <ProtectedRoute>
              <WordsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/invites" replace />} />
          <Route path="invites" element={<AdminInvitesTab />} />
          <Route path="users" element={<AdminUsersTab />} />
          <Route path="vocab" element={<AdminVocabTab />} />
          <Route path="usage" element={<AdminUsageTab />} />
          <Route
            path="stats"
            element={
              <Suspense fallback={<StatsFallback />}>
                <AdminStatsTab />
              </Suspense>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
